import React, { useState } from "react";
import PropTypes from "prop-types";
import { useFileUpload, UPLOAD_STATUS, SYNC_TRIGGER_STATUS } from "./hooks/useFileUpload";
import FileDropZone from "./components/FileDropZone";
import UploadProgressPanel from "./components/UploadProgressPanel";
import {
  CloudUploadIcon,
  InfoIcon,
  VideoFileSmallIcon,
  SrtFileIcon,
  UploadArrowIcon,
  CheckCircleFilledIcon,
  SyncIcon,
  RetryIcon,
  CancelIcon,
  ArrowLeftIcon,
} from "../../icons";
import "./UploadPage.scss";

function UploadPage({ serviceConfig, userEmail, onBack, onStartSync }) {
  const {
    videoFile,
    videoError,
    handleVideoSelect,
    clearVideo,
    originalSrtFile,
    originalSrtError,
    handleOriginalSrtSelect,
    clearOriginalSrt,
    isFormValid,
    uploadStatus,
    videoProgress,
    srtProgress,
    uploadError,
    jobId,
    videoKey,
    srtKey,
    syncEnabled,
    startUpload,
    cancelUpload,
    resetUpload,
    triggerSync,
    syncTriggerStatus,
    syncTriggerError: _syncTriggerError,
  } = useFileUpload({ ...serviceConfig, userEmail });

  // Local error shown beneath the Start Synchronisation button
  const [syncBtnError, setSyncBtnError] = useState("");

  const isIdle = uploadStatus === UPLOAD_STATUS.IDLE;
  const isUploading = uploadStatus === UPLOAD_STATUS.UPLOADING;
  const isVerifying = uploadStatus === UPLOAD_STATUS.VERIFYING;
  const isComplete = uploadStatus === UPLOAD_STATUS.UPLOAD_COMPLETE;
  const isFailed = uploadStatus === UPLOAD_STATUS.UPLOAD_FAILED;

  // Zones and file selectors are locked while upload is in flight or done
  const zonesDisabled = isUploading || isVerifying || isComplete;

  // Progress panel is visible whenever we leave IDLE
  const showProgress = !isIdle;

  // Fires generate_srt_files fetch ONLY after verifying both files are in S3,
  // then hands off to the Sync page on success.
  const handleStartSync = async () => {
    if (!syncEnabled || !onStartSync) return;
    setSyncBtnError("");

    try {
      //              both files have been verified in S3.
      await triggerSync();

      // Strip extension so download page can show "<basename>.zip"
      const videoFileName = videoFile?.name
        ? videoFile.name.replace(/\.[^.]+$/, "")
        : "AV_Sync_Output";

      onStartSync({ jobId, videoKey, srtKey, videoFileName });
    } catch (err) {
      setSyncBtnError(err.message || "Failed to start synchronisation. Please try again.");
    }
  };

  const isTriggering = syncTriggerStatus === SYNC_TRIGGER_STATUS.TRIGGERING;

  return (
    <div className="up-root">
      {/* ── Success alert ─────────────────────────────────────────────────── */}
      {isComplete && (
        <div className="up-alert up-alert--success" role="status">
          <CheckCircleFilledIcon size={17} />
          <div className="up-alert-body">
            <strong>Upload verified successfully</strong>
            <span>
              Both files are confirmed in S3. Click &ldquo;Start Synchronisation&rdquo; to begin
              processing.
            </span>
          </div>
        </div>
      )}

      {/* ── Page Header ───────────────────────────────────────────────────── */}
      <div className="up-page-header">
        <div className="up-page-header-icon">
          <CloudUploadIcon size={22} />
        </div>
        <div className="up-page-header-text">
          <h1 className="up-page-title">Upload Files</h1>
          <p className="up-page-sub">
            Upload your original MP4 video file and corresponding SRT file to get started.
          </p>
        </div>
      </div>

      {/* ── Main Card ─────────────────────────────────────────────────────── */}
      <div className="up-card">
        <div className="up-card-body">
          {/* ── Card heading + info banner ── */}
          <h2 className="up-section-title">Required Files</h2>
          <div className="up-info-banner">
            <InfoIcon size={16} />
            <span>Please upload both files to proceed.</span>
          </div>

          {/* ── Two-column drop zones ── */}
          <div className="up-zones-grid">
            {/* MP4 Zone */}
            <div className="up-zone-col">
              <div className="up-zone-label">
                <VideoFileSmallIcon size={16} />
                <span>Original MP4 File</span>
                <span className="up-req-star">*</span>
              </div>
              <FileDropZone
                id="upload-video"
                accept=".mp4,video/mp4"
                file={videoFile}
                error={videoError}
                progress={
                  isComplete ? 100 : isVerifying ? 100 : isUploading ? videoProgress : undefined
                }
                onFileSelect={handleVideoSelect}
                onClear={clearVideo}
                disabled={zonesDisabled}
                dropLabel="Drag & drop your MP4 file here"
              />
              <div className="up-zone-meta">
                <span>Supported format: MP4</span>
                <span className="up-zone-meta-size">
                  <InfoIcon size={11} />
                  Max size: 1 GB
                </span>
              </div>
            </div>

            {/* SRT Zone */}
            <div className="up-zone-col">
              <div className="up-zone-label">
                <SrtFileIcon size={16} />
                <span>Original SRT File</span>
                <span className="up-req-star">*</span>
              </div>
              <FileDropZone
                id="upload-srt"
                accept=".srt"
                file={originalSrtFile}
                error={originalSrtError}
                progress={
                  isComplete ? 100 : isVerifying ? 100 : isUploading ? srtProgress : undefined
                }
                onFileSelect={handleOriginalSrtSelect}
                onClear={clearOriginalSrt}
                disabled={zonesDisabled}
                dropLabel="Drag & drop your SRT file here"
              />
              <div className="up-zone-meta">
                <span>Supported format: SRT</span>
                <span className="up-zone-meta-size">
                  <InfoIcon size={11} />
                  Max size: 50 KB
                </span>
              </div>
            </div>
          </div>

          {/* ── Upload Progress Panel (hidden while IDLE) ── */}
          {showProgress && (
            <UploadProgressPanel
              uploadStatus={uploadStatus}
              videoProgress={videoProgress}
              srtProgress={srtProgress}
              uploadError={uploadError}
            />
          )}

          {/* ── Note banner (hidden during / after upload) ── */}
          {isIdle && (
            <div className="up-note-banner">
              <div className="up-note-icon">
                <InfoIcon size={18} />
              </div>
              <p>
                <strong>Note:</strong> Please ensure the MP4 and SRT files share the same base
                filename and are properly synchronised before uploading.
              </p>
            </div>
          )}
        </div>
        {/* end up-card-body */}

        {/* ── Card Footer ───────────────────────────────────────────────── */}
        <div className="up-card-footer">
          {/* ── UPLOAD_COMPLETE → Start Synchronisation (enabled) ── */}
          {isComplete && (
            <div className="up-sync-action">
              <button
                type="button"
                className="up-upload-btn up-upload-btn--sync"
                onClick={handleStartSync}
                disabled={!syncEnabled || isTriggering}
                aria-busy={isTriggering}
              >
                {isTriggering ? (
                  <>
                    <span className="up-spinner" /> Starting…
                  </>
                ) : (
                  <>
                    <SyncIcon size={17} /> Start Synchronisation
                  </>
                )}
              </button>
              {syncBtnError && (
                <p className="up-sync-error" role="alert">
                  {syncBtnError}
                </p>
              )}
            </div>
          )}

          {/* ── UPLOAD_FAILED → Try Again ── */}
          {isFailed && (
            <div className="up-footer-row">
              <button
                type="button"
                className="up-upload-btn up-upload-btn--ghost"
                onClick={resetUpload}
              >
                <RetryIcon size={16} /> Try Again
              </button>
              {/* Start Sync stays disabled on failure */}
              <button
                type="button"
                className="up-upload-btn up-upload-btn--sync"
                disabled
                title="Upload must succeed before starting synchronisation"
              >
                <SyncIcon size={17} /> Start Synchronisation
              </button>
            </div>
          )}

          {/* ── UPLOADING → Uploading… + Cancel ── */}
          {isUploading && (
            <div className="up-footer-row">
              <button
                type="button"
                className="up-upload-btn up-upload-btn--ghost"
                onClick={cancelUpload}
              >
                <CancelIcon size={16} /> Cancel
              </button>
              <button type="button" className="up-upload-btn" disabled>
                <span className="up-spinner" /> Uploading…
              </button>
            </div>
          )}

          {/* ── VERIFYING → Verifying… (both disabled) ── */}
          {isVerifying && (
            <button type="button" className="up-upload-btn" disabled>
              <span className="up-spinner" /> Verifying…
            </button>
          )}

          {/* ── IDLE → Upload Files ── */}
          {isIdle && (
            <div className="up-footer-row">
              {onBack && (
                <button
                  type="button"
                  className="up-back-btn"
                  onClick={onBack}
                  aria-label="Back to Services"
                >
                  <ArrowLeftIcon size={15} />
                  Back
                </button>
              )}
              <button
                type="button"
                className="up-upload-btn"
                onClick={startUpload}
                disabled={!isFormValid}
              >
                <UploadArrowIcon size={17} /> Upload Files
              </button>
            </div>
          )}
        </div>
        {/* end up-card-footer */}
      </div>
      {/* end up-card */}
    </div>
  );
}

UploadPage.propTypes = {
  serviceConfig: PropTypes.object,
  userEmail: PropTypes.string,
  onBack: PropTypes.func,
  onStartSync: PropTypes.func,
};

export default UploadPage;
